import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { DateTime } from 'luxon';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { createSearchParams, useSearchParams, useNavigate } from 'react-router-dom';
import { baseUrl, apiHelper } from 'helpers/util';

import RowBox from 'components/RowBox';
import SmallHeading from 'components/SmallHeading';

export default function Form(props) {
    const warehouse = props.warehouse;
    const navigate = useNavigate();
    const [ balanceRows, setBalanceRows ] = useState([]);
    const [ startDate, setStartDate ] = useState(DateTime.now().startOf('month'))
    const [ endDate, setEndDate ] = useState(DateTime.now())

    const [ qtyAmtOption, setQtyAmtOption ] = useState('Quantity')
    const [ fieldsVisibility, setFieldsVisibility ] = useState('All fields')

    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ totalsRow, setTotalsRow ] = useState({});

    const loadBalanceReport = function() {
        const apiParams = { "warehouse": warehouse,
            "start_date": startDate.toFormat('yyyy-MM-dd'),
            "end_date": endDate.toFormat('yyyy-MM-dd'),
            "route": "store_manager.store_balance" }
        apiHelper.get({ "url": `${baseUrl}/.netlify/functions/erp_custom_api`,
            "params": apiParams })
        .then(response => {
            if (response.status) {
                setBalanceRows(response.json.message.balance_rows);

                // computing total row
                const totalsData = {
                    opening_balance_total: 0,
                    stock_in_total: 0,
                    stock_out_total: 0,
                    closing_balance_total: 0,
                }
                for (const r of response.json.message.balance_rows) {
                    totalsData.opening_balance_total += (r.opening_balance * r.cost);
                    totalsData.stock_in_total += (r.stock_in_quantity * r.cost);
                    totalsData.stock_out_total += (r.stock_out_quantity * r.cost);
                    totalsData.closing_balance_total += (r.closing_balance * r.cost);
                }
                setTotalsRow(totalsData);
            } else { console.log(response.json); }
        })
    }

    const getEntryFontColor = function(entryType) {
        if (entryType == 'STOCK IN') return 'green';
        if (entryType == 'STOCK OUT') return 'red';
        return '#145DA0';
    }
    const getAmtOrQty = function(quantity, row) {
        const multiplier = (qtyAmtOption == 'Amount' && row.cost ? row.cost : 1);
        const currencyIndicator = qtyAmtOption == 'Amount' ? '₹ ' : ''
        const amount = Number((quantity * multiplier).toFixed(2))
        return `${currencyIndicator}${amount}`;
    }

    useEffect(function() {
        loadBalanceReport();
    }, [])

    return (
        <Box sx={{ padding: '15px' }}>
            <SmallHeading>Balance Report</SmallHeading>
            <RowBox sx={{ width: '100%' }}>
                <Box sx={{ marginRight: '10px', marginTop: '15px' }}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <MobileDatePicker
                            onChange={(newValue) => { setStartDate(newValue); }}
                            value={startDate}
                            inputFormat="dd-MMM-yyyy"
                            mask=""
                            closeOnSelect={true}
                            disableMaskedInput={true}
                            renderInput={(params) => <TextField {...params}
                                label="Start Date" size="small" sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Box>
                <Box sx={{ marginRight: '10px', marginTop: '15px' }}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <MobileDatePicker
                            onChange={(newValue) => { setEndDate(newValue); }}
                            value={endDate}
                            inputFormat="dd-MMM-yyyy"
                            mask=""
                            closeOnSelect={true}
                            disableMaskedInput={true}
                            renderInput={(params) => <TextField {...params}
                                label="End Date" size="small" sx={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Box>

                <Button sx={{ marginRight: '10px', marginTop: '15px' }}
                    onClick={() => loadBalanceReport()} size="small"
                    variant="outlined">Search</Button>
            </RowBox>
            <RowBox sx={{ 'marginTop': '10px' }}>
                <ToggleButtonGroup
                    size="small" sx={{ marginRight: '10px' }}
                    orientation="horizontal" value={qtyAmtOption}
                    exclusive onChange={ (e, v) => setQtyAmtOption(v) }>
                    <ToggleButton value="Quantity">Quantity</ToggleButton>
                    <ToggleButton value="Amount">Amount</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                    size="small"
                    orientation="horizontal" value={fieldsVisibility}
                    exclusive onChange={ (e, v) => setFieldsVisibility(v) }>
                    <ToggleButton value="All fields">All fields</ToggleButton>
                    <ToggleButton value="Balance only">Balance only</ToggleButton>
                </ToggleButtonGroup>
            </RowBox>
            <Box sx={{ width: '100%', overflowX: 'scroll' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ 
                                '& td, & th': { fontSize: '17px', 'fontWeight': '700' } }}>
                            <TableCell>Product</TableCell>
                            <TableCell>Unit</TableCell>
                            { fieldsVisibility == 'All fields' &&
                            <>
                                <TableCell align="right">Opening</TableCell>
                                <TableCell sx={{ 'color': getEntryFontColor('STOCK IN') }}
                                    align="right">STOCK IN</TableCell>
                                <TableCell sx={{ 'color': getEntryFontColor('STOCK OUT') }}
                                    align="right">STOCK OUT</TableCell>
                            </> }
                            <TableCell sx={{ 'color': getEntryFontColor('') }}
                                align="right">Balance</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                    {balanceRows.map((e) => (
                        <TableRow key={e.product_id}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 },
                                '& td, & th': { fontSize: '15px' } }}>
                                <TableCell component="th" scope="row">{e.product_name}</TableCell>
                                <TableCell component="th" scope="row">{e.unit}</TableCell>
                                { fieldsVisibility == 'All fields' &&
                                <>
                                    <TableCell align="right">{ getAmtOrQty(e.opening_balance, e)}</TableCell>
                                    <TableCell sx={{ 'color': getEntryFontColor('STOCK IN') }} align="right">
                                        { getAmtOrQty(e.stock_in_quantity, e)}</TableCell>
                                    <TableCell sx={{ 'color': getEntryFontColor('STOCK OUT') }} align="right">
                                        { getAmtOrQty(e.stock_out_quantity, e)}</TableCell>
                                </> }
                                <TableCell sx={{ 'color': getEntryFontColor(''), 'fontWeight': '700' }}
                                    align="right">{ getAmtOrQty(e.closing_balance, e)}</TableCell>
                        </TableRow>
                    ))}
                    </TableBody>
                    { qtyAmtOption == 'Amount' &&
                    <TableFooter>
                        <TableRow
                            sx={{ '& td, & th': { fontSize: '17px', borderTop: '1px solid black' } }}>
                                <TableCell component="th" scope="row">Total</TableCell>
                                <TableCell component="th" scope="row"></TableCell>
                                { fieldsVisibility == 'All fields' &&
                                <>
                                    <TableCell align="right">{`₹ ${totalsRow.opening_balance_total}`}</TableCell>
                                    <TableCell sx={{ 'color': getEntryFontColor('STOCK IN') }} align="right">
                                        {`₹ ${totalsRow.stock_in_total}`}</TableCell>
                                    <TableCell sx={{ 'color': getEntryFontColor('STOCK OUT') }} align="right">
                                        {`₹ ${totalsRow.stock_out_total}`}</TableCell>
                                </> }
                                <TableCell sx={{ 'color': getEntryFontColor(''), 'fontWeight': '700' }}
                                    align="right">{`₹ ${totalsRow.closing_balance_total}`}</TableCell>
                        </TableRow>
                    </TableFooter>
                    }
                </Table>
            </Box>
        </Box>
    );
}
